/* ── Hero Banner ─────────────────────────────────────────────────────────── */

class HeroBanner extends HTMLElement {
  connectedCallback() {
    this.slides  = Array.from(this.querySelectorAll('.hhb__slide'));
    this.dots    = Array.from(this.querySelectorAll('.hhb__dot'));
    this.track   = this.querySelector('.hhb__track');

    if (!this.slides.length) return;

    this.currentIndex     = -1; // -1 = no slide active yet
    this.timer            = null;
    this.isPaused         = false;
    this._videoEndedBound = null;
    this._activeVideo     = null;

    // Ensure no video loops and attributes are correct
    this.querySelectorAll('video').forEach(v => {
      v.loop = false;
      v.removeAttribute('loop');
      v.muted = true;
      v.setAttribute('playsinline', '');
    });

    // Arrow buttons
    const prev = this.querySelector('.hhb__arrow--prev');
    const next = this.querySelector('.hhb__arrow--next');
    prev?.addEventListener('click', () => this._navigate(-1));
    next?.addEventListener('click', () => this._navigate(1));

    // Dot buttons
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this._goTo(i)));

    // Pause on hover (image slides only)
    if (this.dataset.pauseOnHover === 'true') {
      this.addEventListener('mouseenter', () => this._onHoverEnter());
      this.addEventListener('mouseleave', () => this._onHoverLeave());
    }

    // Keyboard accessibility
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  this._navigate(-1);
      if (e.key === 'ArrowRight') this._navigate(1);
    });

    // Slide 0 exists only for the "adapt to first image" ratio calculation.
    // Hide its dot and start the visible carousel on slide 1.
    if (this.dots[0]) this.dots[0].style.display = 'none';
    this._activateSlide(1);
  }

  _activateSlide(index) {
    const total = this.slides.length;
    const next  = ((index % total) + total) % total;
    const prev  = this.currentIndex;

    // Tear down previous slide before switching
    if (prev >= 0) this._teardownSlide(this.slides[prev]);

    this.currentIndex = next;
    const slide = this.slides[next];

    // Apply visual transition
    if (this.dataset.transition === 'slide') {
      this.slides.forEach(s => s.classList.remove('is-active'));
      slide.classList.add('is-active');
      this.track.style.transform = `translateX(-${next * 100}%)`;
    } else {
      // crossfade
      if (prev >= 0) this.slides[prev].classList.remove('is-active');
      slide.classList.add('is-active');
    }

    // Update dots
    this.dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === next);
      d.setAttribute('aria-selected', i === next ? 'true' : 'false');
    });

    // Restart CSS animations: remove playing, force reflow, add playing
    this.slides.forEach(s => s.classList.remove('playing'));
    void slide.offsetWidth; // trigger reflow so animations restart
    slide.classList.add('playing');

    // Start the slide (video timer or image timer)
    this._startSlide(slide);
  }

  _startSlide(slide) {
    const video = slide.querySelector('video');

    if (video) {
      // Video slide: reset to start, play, advance when ended
      video.currentTime = 0;
      if (!video.ended) {
        video.play().catch(() => {
          // Autoplay blocked — advance after a fallback duration
          this.timer = setTimeout(() => this._navigate(1), 5000);
        });
      }
      this._activeVideo = video;
      this._videoEndedBound = () => this._navigate(1);
      video.addEventListener('ended', this._videoEndedBound, { once: true });
    } else {
      // Image slide: advance after configured duration (unless hover-paused)
      if (!this.isPaused) {
        const duration = (parseInt(slide.dataset.duration) || 5) * 1000;
        this.timer = setTimeout(() => this._navigate(1), duration);
      }
    }
  }

  _teardownSlide(slide) {
    if (!slide) return;

    // Clear any pending image timer
    clearTimeout(this.timer);
    this.timer = null;

    // Stop video and detach ended listener
    const video = slide.querySelector('video');
    if (video) {
      if (this._videoEndedBound) {
        video.removeEventListener('ended', this._videoEndedBound);
        this._videoEndedBound = null;
      }
      video.pause();
      this._activeVideo = null;
    }
  }

  _navigate(dir) {
    const total = this.slides.length;
    let next = this.currentIndex + dir;
    // Index 0 is reserved for the adapt-height ratio — skip it in navigation.
    // Wrap within [1, total-1].
    if (next < 1) next = total - 1;
    if (next >= total) next = 1;
    this._activateSlide(next);
  }

  _goTo(index) {
    // Index 0 is not navigable by the user
    if (index > 0 && index !== this.currentIndex) this._activateSlide(index);
  }

  _onHoverEnter() {
    // Only pause image slides — video slides always play through
    const slide = this.slides[this.currentIndex];
    if (slide && !slide.querySelector('video')) {
      this.isPaused = true;
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  _onHoverLeave() {
    this.isPaused = false;
    const slide = this.slides[this.currentIndex];
    if (slide && !slide.querySelector('video')) {
      const duration = (parseInt(slide.dataset.duration) || 5) * 1000;
      this.timer = setTimeout(() => this._navigate(1), duration);
    }
  }
}

customElements.define('hero-banner', HeroBanner);
